import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Button, Modal, Form, Table } from "react-bootstrap";
import axios from "axios";
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import Loading from "react-fullscreen-loading";
import { toast } from "react-hot-toast";
import ShowTime from "../../../utils/ShowTime/ShowTime";
import MapProduct from "../../../utils/ProductMapper/MapProduct";
import { FaRedo } from "react-icons/fa";
import { useAuth, Can } from "../../../authentication/AuthContext";

const PRODUCTS = [
  { value: "SPCAD_ACAD_Beta", label: "AUTOCAD" },
  { value: "SPCAD_CADMATE", label: "CADMATE" },
  { value: "SPCAD_GSTARCAD", label: "GSTARCAD" },
  { value: "SPCAD_BRX", label: "BRICSCAD" },
  { value: "SPCAD_ZWCAD", label: "ZWCAD" },
  { value: "SPCAD_PCAD", label: "PCAD" },
];

const licenseStatus = (expiresAt) => {
  const exp = new Date(expiresAt);
  const now = new Date();
  if (exp <= now) return { label: "Expired", bg: "#fff0f0", color: "#dc3545" };
  const diff = (exp - now) / (1000 * 60 * 60 * 24);
  if (diff <= 90) return { label: "Expiring Soon", bg: "#fff8e1", color: "#b45309" };
  return { label: "Active", bg: "#e6f7ef", color: "#016641" };
};

const DAYS_PER_UNIT = { days: 1, months: 30, years: 365 };
const DURATION_UNIT_OPTIONS = [
  { value: "days",   label: "Days" },
  { value: "months", label: "Months" },
  { value: "years",  label: "Years" },
];

const durationToDays = (value, unit) => {
  const v = parseInt(value, 10);
  if (!v || v < 1) return 0;
  return v * (DAYS_PER_UNIT[unit] || 0);
};

const addDaysToDate = (dateStr, days) => {
  const d = dateStr && new Date(dateStr) > new Date() ? new Date(dateStr) : new Date();
  d.setDate(d.getDate() + days);
  return d;
};

export default function UserShow() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Server-verified userType (NOT localStorage, which is editable).
  const { userType } = useAuth();
  const isReseller = userType === "reseller";

  const [screenloader, setScreenloader] = useState(true);
  const [userData, setUserData] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [availableCredits, setAvailableCredits] = useState(0);

  // Create license modal
  const [showCreate, setShowCreate] = useState(false);
  const [createProduct, setCreateProduct] = useState(PRODUCTS[0].value);
  const [createValue, setCreateValue] = useState(1);
  const [createUnit, setCreateUnit] = useState("years");
  const [creating, setCreating] = useState(false);
  const [createNotify, setCreateNotify] = useState(true);

  // Renew modal
  const [showRenew, setShowRenew] = useState(false);
  const [renewingLicense, setRenewingLicense] = useState(null);
  const [renewValue, setRenewValue] = useState(1);
  const [renewUnit, setRenewUnit] = useState("years");
  const [renewing, setRenewing] = useState(false);
  const [renewNotify, setRenewNotify] = useState(true);

  const headers = { "access-token": localStorage.getItem("accessToken") };
  const base = process.env.REACT_APP_API_URL;

  const fetchData = async () => {
    try {
      if (isReseller) {
        const res = await axios.get(`${base}/reseller/users/${id}`, { headers });
        setScreenloader(false);
        if (res.data.status) {
          setUserData(res.data.user);
          setLicenses(res.data.licenses || []);
          setAvailableCredits(res.data.availableCredits ?? 0);
        } else {
          toast.error(res.data.message || "User not found");
          navigate("/Admin/Users");
        }
      } else {
        const res = await axios.get(`${base}/admin/users/${id}/invoice`, { headers });
        setScreenloader(false);
        if (res.data.status && res.data.data?.[0]) {
          const d = res.data.data[0];
          setUserData(d);
          setLicenses(d.licenses || []);
        } else {
          toast.error("User not found");
          navigate("/Admin/Users");
        }
      }
    } catch {
      setScreenloader(false);
      toast.error("Something went wrong");
    }
  };

  useEffect(() => { fetchData(); }, [id]); // eslint-disable-line

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await axios.post(
        `${base}/reseller/users/${id}/licenses`,
        { product: createProduct, duration_value: createValue, duration_unit: createUnit, notify_customer: createNotify },
        { headers }
      );
      setCreating(false);
      if (res.data.status) {
        if (res.data.email_error) {
          toast.error(res.data.message);
        } else {
          toast.success(res.data.message);
        }
        setShowCreate(false);
        setCreateProduct(PRODUCTS[0].value);
        setCreateValue(1);
        setCreateUnit("years");
        setCreateNotify(true);
        fetchData();
      } else {
        toast.error(res.data.message);
      }
    } catch {
      setCreating(false);
      toast.error("Something went wrong");
    }
  };

  const handleRenew = async (e) => {
    e.preventDefault();
    setRenewing(true);
    try {
      const res = await axios.post(
        `${base}/reseller/licenses/${renewingLicense.id}/renew`,
        { duration_value: renewValue, duration_unit: renewUnit, notify_customer: renewNotify },
        { headers }
      );
      setRenewing(false);
      if (res.data.status) {
        if (res.data.email_error) {
          toast.error(res.data.message);
        } else {
          toast.success(res.data.message);
        }
        setShowRenew(false);
        setRenewingLicense(null);
        setRenewValue(1);
        setRenewUnit("years");
        fetchData();
      } else {
        toast.error(res.data.message);
      }
    } catch {
      setRenewing(false);
      toast.error("Something went wrong");
    }
  };

  const createCredits = durationToDays(createValue, createUnit);
  const renewCredits = durationToDays(renewValue, renewUnit);
  const renewNewExpiry = renewingLicense ? addDaysToDate(renewingLicense.expires_at, renewCredits) : null;

  const infoRows = userData ? [
    ["Email", userData.email],
    ["Name", `${userData.fname || ""} ${userData.lname || ""}`.trim() || "—"],
    ["Organization", userData.organization || "—"],
    ["Country", userData.country || "—"],
    ["Phone", userData.phone_number || "—"],
    ["Ref ID", userData.user_reference_id || "—"],
    ["Verified", userData.verified ? "Yes" : "No"],
    ["Joined", ShowTime(userData.created_at)],
    ["Last Login", ShowTime(userData.login_at)],
  ] : [];

  return (
    <div>
      <AdminDashboardLayout />
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className="content-page-bg">
        <div className="content-page">
          <div className="content">

            {/* Header */}
            <div className="row mb-3 align-items-center">
              <div className="col">
                <h2>User Detail</h2>
                {userData && (
                  <p className="text-muted mb-0">{userData.email}</p>
                )}
              </div>
              <div className="col-auto d-flex gap-2">
                {/* {isReseller && (
                  <Button className="admin-btn" onClick={() => { setCreateNotify(true); setShowCreate(true); }}>
                    + Create License
                  </Button>
                )} */}
                <Can permission="admin.users.update">
                  <Button
                    className="admin-btn"
                    onClick={() => navigate(`/Admin/Users/${id}/edit`)}
                  >
                    Edit User
                  </Button>
                </Can>
                <Button
                  style={{ backgroundColor: "#5f75a5", color: "#fff", border: "none" }}
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>
              </div>
            </div>

            {/* User info card */}
            {userData && (
              <Card className="mb-4">
                <Card.Body>
                  <div className="row g-3">
                    {infoRows.map(([label, val]) => (
                      <div className="col-6 col-md-3" key={label}>
                        <div className="text-muted" style={{ fontSize: "12px", fontWeight: 600 }}>{label}</div>
                        <div style={{ fontSize: "14px" }}>{val}</div>
                      </div>
                    ))}
                    {/* {isReseller && (
                      <div className="col-6 col-md-3">
                        <div className="text-muted" style={{ fontSize: "12px", fontWeight: 600 }}>YOUR AVAILABLE CREDITS</div>
                        <div style={{ fontSize: "20px", fontWeight: 700, color: availableCredits > 0 ? "#016641" : "#dc3545" }}>
                          {availableCredits.toLocaleString()}
                        </div>
                      </div>
                    )} */}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Licenses table */}
            <Card>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Licenses ({licenses.length})</h5>
                  <Can permission="reseller.licenses.assign">
                    <Button size="sm" className="admin-btn" onClick={() => { setCreateNotify(true); setShowCreate(true); }}>
                      + New License
                    </Button>
                  </Can>
                </div>
                {licenses.length === 0 ? (
                  <p className="text-muted" style={{ fontSize: "14px" }}>No licenses yet.</p>
                ) : (
                  <div className="table-responsive">
                    <Table hover size="sm">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Ref ID</th>
                          <th>Product</th>
                          <th>Start Date</th>
                          <th>Expiry</th>
                          <th>Status</th>
                          <th>Machine User</th>
                          <th>Version</th>
                          <th>Last Action</th>
                          {isReseller && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {licenses.map((l, i) => {
                          const s = licenseStatus(l.expires_at);
                          return (
                            <tr key={l.id}>
                              <td style={{ fontSize: "13px" }}>{i + 1}</td>
                              <td style={{ fontFamily: "monospace", fontSize: "12px" }}>{l.license_reference_id}</td>
                              <td style={{ fontSize: "13px" }}>{MapProduct ? MapProduct(l.product, "simple") : l.product}</td>
                              <td style={{ fontSize: "13px", whiteSpace: "nowrap" }}>{ShowTime(l.created_at)}</td>
                              <td style={{ fontSize: "13px", whiteSpace: "nowrap" }}>{ShowTime(l.expires_at)}</td>
                              <td>
                                <span style={{
                                  display: "inline-block", padding: "2px 10px", borderRadius: "10px",
                                  fontSize: "12px", fontWeight: 600, backgroundColor: s.bg, color: s.color,
                                }}>
                                  {s.label}
                                </span>
                              </td>
                              <td style={{ fontSize: "13px" }}>{l.machine_user || "—"}</td>
                              <td style={{ fontSize: "13px" }}>{l.product_version || "—"}</td>
                              <td style={{ fontSize: "12px", color: "#64748b" }}>{l.last_action || "—"}</td>
                              {isReseller && (
                                <td>
                                  <Can permission="reseller.licenses.renew">
                                    <button
                                      title="Renew license"
                                      style={{
                                        background: "none", border: "none", cursor: "pointer",
                                        color: "#523ba8", padding: "2px 6px", borderRadius: "4px",
                                        fontSize: "13px", display: "flex", alignItems: "center", gap: "4px"
                                      }}
                                      onClick={() => { setRenewingLicense(l); setRenewValue(1); setRenewUnit("years"); setRenewNotify(true); setShowRenew(true); }}
                                    >
                                      <FaRedo /> Renew
                                    </button>
                                  </Can>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>

          </div>
        </div>
      </div>

      {/* ── Create License Modal ── */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create License</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleCreate}>
          <Modal.Body>
            <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
              <div className="text-muted" style={{ fontSize: "12px", fontWeight: 600 }}>USER</div>
              <div style={{ fontSize: "14px", fontWeight: 600 }}>{userData?.email}</div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, fontSize: "14px" }}>Product</Form.Label>
              <Form.Select
                value={createProduct}
                onChange={(e) => setCreateProduct(e.target.value)}
                required
              >
                {PRODUCTS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ fontWeight: 600, fontSize: "14px" }}>Duration</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="number" min="1"
                  value={createValue}
                  onChange={(e) => setCreateValue(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{ maxWidth: "110px" }}
                />
                <Form.Select
                  value={createUnit}
                  onChange={(e) => setCreateUnit(e.target.value)}
                  style={{ flex: 1 }}
                >
                  {DURATION_UNIT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </div>
            </Form.Group>

            <Form.Check
              className="mb-3"
              type="checkbox"
              id="create-notify-customer"
              label="Email the customer that a new license has been created for them"
              checked={createNotify}
              onChange={(e) => setCreateNotify(e.target.checked)}
            />

            {/* Credit conversion */}
            <div className="p-3 rounded" style={{ backgroundColor: "#f1f5f9", border: "1px solid #cbd5e1" }}>
              <div className="d-flex justify-content-between mb-1">
                <span style={{ fontSize: "13px", color: "#64748b" }}>Credits needed</span>
                <span style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                  {createCredits.toLocaleString()} <span style={{ fontSize: "11px", fontWeight: 400, color: "#64748b" }}>({createValue} {createUnit} × {DAYS_PER_UNIT[createUnit]} days)</span>
                </span>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span style={{ fontSize: "13px", color: "#64748b" }}>Your available credits</span>
                <span style={{ fontSize: "14px", fontWeight: 700, color: availableCredits >= createCredits ? "#016641" : "#dc3545" }}>
                  {availableCredits.toLocaleString()}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span style={{ fontSize: "13px", color: "#64748b" }}>After creation</span>
                <span style={{ fontSize: "14px", fontWeight: 700, color: availableCredits - createCredits >= 0 ? "#374151" : "#dc3545" }}>
                  {(availableCredits - createCredits).toLocaleString()}
                </span>
              </div>
              {availableCredits < createCredits && (
                <div className="mt-2 text-danger" style={{ fontSize: "12px" }}>
                  Insufficient credits. Please contact your account manager.
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              className="admin-btn" type="submit"
              disabled={creating || availableCredits < createCredits}
            >
              {creating ? "Creating…" : `Create License (${createCredits} credits)`}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* ── Renew License Modal ── */}
      <Modal show={showRenew} onHide={() => { setShowRenew(false); setRenewingLicense(null); setRenewValue(1); setRenewUnit("years"); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Renew License</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleRenew}>
          <Modal.Body>
            {renewingLicense && (
              <>
                <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <div className="row g-2">
                    <div className="col-6">
                      <div className="text-muted" style={{ fontSize: "11px", fontWeight: 600 }}>LICENSE</div>
                      <div style={{ fontFamily: "monospace", fontSize: "12px" }}>{renewingLicense.license_reference_id}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted" style={{ fontSize: "11px", fontWeight: 600 }}>PRODUCT</div>
                      <div style={{ fontSize: "13px" }}>{MapProduct ? MapProduct(renewingLicense.product, "simple") : renewingLicense.product}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted" style={{ fontSize: "11px", fontWeight: 600 }}>CURRENT EXPIRY</div>
                      <div style={{ fontSize: "13px" }}>{ShowTime(renewingLicense.expires_at)}</div>
                    </div>
                    <div className="col-6">
                      <div className="text-muted" style={{ fontSize: "11px", fontWeight: 600 }}>NEW EXPIRY</div>
                      <div style={{ fontSize: "13px", fontWeight: 700, color: "#016641" }}>
                        {renewNewExpiry ? ShowTime(renewNewExpiry.toISOString()) : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600, fontSize: "14px" }}>Duration to add</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="number" min="1"
                      value={renewValue}
                      onChange={(e) => setRenewValue(Math.max(1, parseInt(e.target.value) || 1))}
                      style={{ maxWidth: "110px" }}
                    />
                    <Form.Select
                      value={renewUnit}
                      onChange={(e) => setRenewUnit(e.target.value)}
                      style={{ flex: 1 }}
                    >
                      {DURATION_UNIT_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </Form.Select>
                  </div>
                </Form.Group>

                <Form.Check
                  className="mb-3"
                  type="checkbox"
                  id="renew-notify-customer"
                  label="Email the customer that their license has been renewed"
                  checked={renewNotify}
                  onChange={(e) => setRenewNotify(e.target.checked)}
                />

                {/* Credit conversion */}
                <div className="p-3 rounded" style={{ backgroundColor: "#f1f5f9", border: "1px solid #cbd5e1" }}>
                  <div className="d-flex justify-content-between mb-1">
                    <span style={{ fontSize: "13px", color: "#64748b" }}>Credits needed</span>
                    <span style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                      {renewCredits.toLocaleString()} <span style={{ fontSize: "11px", fontWeight: 400, color: "#64748b" }}>({renewValue} {renewUnit} × {DAYS_PER_UNIT[renewUnit]} days)</span>
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span style={{ fontSize: "13px", color: "#64748b" }}>Your available credits</span>
                    <span style={{ fontSize: "14px", fontWeight: 700, color: availableCredits >= renewCredits ? "#016641" : "#dc3545" }}>
                      {availableCredits.toLocaleString()}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span style={{ fontSize: "13px", color: "#64748b" }}>After renewal</span>
                    <span style={{ fontSize: "14px", fontWeight: 700, color: availableCredits - renewCredits >= 0 ? "#374151" : "#dc3545" }}>
                      {(availableCredits - renewCredits).toLocaleString()}
                    </span>
                  </div>
                  {availableCredits < renewCredits && (
                    <div className="mt-2 text-danger" style={{ fontSize: "12px" }}>
                      Insufficient credits. Please contact your account manager.
                    </div>
                  )}
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowRenew(false); setRenewingLicense(null); setRenewValue(1); setRenewUnit("years"); }}>Cancel</Button>
            <Button
              className="admin-btn" type="submit"
              disabled={renewing || availableCredits < renewCredits}
            >
              {renewing ? "Renewing…" : `Renew License (${renewCredits} credits)`}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
